import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaPaperPlane, FaArrowLeft, FaPlus, FaSearch, FaEnvelope, FaCircle } from 'react-icons/fa';
import '../styles/MessagesPage.css';

const API = process.env.REACT_APP_API_URL || '';

function MessagesPage({ user }) {
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showNewConv, setShowNewConv] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchUser, setSearchUser] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [firstMessage, setFirstMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef();
  const pollRef = useRef();

  useEffect(() => {
    loadConversations();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (activeConv) {
      loadMessages(activeConv);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => loadMessages(activeConv), 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeConv]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = async () => {
    try {
      const res = await axios.get(`${API}/api/messages/conversations`);
      setConversations(res.data);
    } catch (e) {}
  };

  const loadMessages = async (convId) => {
    try {
      const res = await axios.get(`${API}/api/messages/conversations/${convId}`);
      setMessages(res.data.messages);
      setMembers(res.data.members);
      loadConversations(); // refresh unread counts
    } catch (e) {}
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConv) return;
    try {
      await axios.post(`${API}/api/messages/conversations/${activeConv}/send`, { content: newMessage });
      setNewMessage('');
      loadMessages(activeConv);
    } catch (e) { console.error(e); }
  };

  const openNewConv = async () => {
    setShowNewConv(true);
    try {
      const res = await axios.get(`${API}/api/messages/users`);
      setUsers(res.data);
    } catch (e) {}
  };

  const startConversation = async () => {
    if (!selectedUser || !firstMessage.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/messages/conversations`, {
        user_id: selectedUser.id,
        message: firstMessage
      });
      setShowNewConv(false);
      setSelectedUser(null);
      setFirstMessage('');
      setSearchUser('');
      await loadConversations();
      setActiveConv(res.data.conversation_id);
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur');
    } finally { setLoading(false); }
  };

  const getConvName = (conv) => {
    if (!conv.members || conv.members.length === 0) return 'Conversation';
    return conv.members.map(m => m.display_name || m.username).join(', ');
  };

  const formatTime = (d) => {
    const date = new Date(d);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'Maintenant';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}min`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    if (diff < 604800000) return date.toLocaleDateString('fr-FR', { weekday: 'short' });
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const formatMsgTime = (d) => {
    return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const filteredUsers = users.filter(u =>
    u.username.toLowerCase().includes(searchUser.toLowerCase()) ||
    (u.display_name && u.display_name.toLowerCase().includes(searchUser.toLowerCase()))
  );

  const otherMembers = members.filter(m => m.id !== user?.id);

  return (
    <div className="messages-page">
      <div className="messages-sidebar">
        <div className="sidebar-header">
          <h2><FaEnvelope /> Messages</h2>
          <button className="new-conv-btn" onClick={openNewConv}><FaPlus /></button>
        </div>
        <div className="conv-list">
          {conversations.length === 0 ? (
            <div className="conv-empty">Aucune conversation</div>
          ) : conversations.map(c => (
            <div key={c.id} className={`conv-item ${activeConv === c.id ? 'active' : ''} ${c.unread_count > 0 ? 'unread' : ''}`}
              onClick={() => { setActiveConv(c.id); setShowNewConv(false); }}>
              <div className="conv-avatar">{getConvName(c).charAt(0).toUpperCase()}</div>
              <div className="conv-info">
                <div className="conv-name">{getConvName(c)}</div>
                <div className="conv-preview">{c.last_message ? c.last_message.substring(0, 40) + (c.last_message.length > 40 ? '...' : '') : ''}</div>
              </div>
              <div className="conv-meta">
                <div className="conv-time">{c.last_message_at ? formatTime(c.last_message_at) : ''}</div>
                {c.unread_count > 0 && <div className="conv-unread">{c.unread_count}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="messages-main">
        {showNewConv ? (
          <div className="new-conv-panel">
            <div className="new-conv-header">
              <button onClick={() => setShowNewConv(false)}><FaArrowLeft /></button>
              <h3>Nouveau message</h3>
            </div>
            <div className="new-conv-search">
              <FaSearch />
              <input value={searchUser} onChange={e => setSearchUser(e.target.value)} placeholder="Rechercher un utilisateur..." />
            </div>
            {selectedUser ? (
              <div className="new-conv-selected">
                <span>À : <strong>{selectedUser.display_name || selectedUser.username}</strong></span>
                <button onClick={() => setSelectedUser(null)}>✕</button>
              </div>
            ) : (
              <div className="user-list">
                {filteredUsers.map(u => (
                  <div key={u.id} className="user-item" onClick={() => setSelectedUser(u)}>
                    <div className="user-avatar">{(u.display_name || u.username).charAt(0).toUpperCase()}</div>
                    <div>
                      <div className="user-display">{u.display_name || u.username}</div>
                      <div className="user-handle">@{u.username}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {selectedUser && (
              <div className="new-conv-compose">
                <textarea value={firstMessage} onChange={e => setFirstMessage(e.target.value)}
                  placeholder="Écrivez votre message..." rows={3} />
                <button onClick={startConversation} disabled={loading || !firstMessage.trim()}>
                  <FaPaperPlane /> Envoyer
                </button>
              </div>
            )}
          </div>
        ) : activeConv ? (
          <>
            <div className="chat-header">
              <button className="chat-back-btn" onClick={() => setActiveConv(null)}><FaArrowLeft /></button>
              <div className="chat-header-info">
                <div className="chat-header-name">{otherMembers.map(m => m.display_name || m.username).join(', ')}</div>
              </div>
            </div>
            <div className="chat-messages">
              {messages.map((m, i) => {
                const isMe = m.sender_id === user?.id;
                const showDate = i === 0 || new Date(m.created_at).toDateString() !== new Date(messages[i - 1].created_at).toDateString();
                return (
                  <React.Fragment key={m.id}>
                    {showDate && (
                      <div className="chat-date-divider">
                        {new Date(m.created_at).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </div>
                    )}
                    <div className={`chat-bubble ${isMe ? 'me' : 'other'}`}>
                      {!isMe && <span className="bubble-sender">{m.display_name || m.username}</span>}
                      <p>{m.content}</p>
                      <span className="bubble-time">{formatMsgTime(m.created_at)}</span>
                    </div>
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            <div className="chat-input">
              <input value={newMessage} onChange={e => setNewMessage(e.target.value)}
                placeholder="Écrire un message..."
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())} />
              <button onClick={sendMessage} disabled={!newMessage.trim()}><FaPaperPlane /></button>
            </div>
          </>
        ) : (
          <div className="chat-placeholder">
            <FaEnvelope className="placeholder-icon" />
            <p>Sélectionnez une conversation ou créez-en une nouvelle</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default MessagesPage;
